import { Autocomplete, CircularProgress, TextField, Paper } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useGetAssets } from "@hooks/use-get/assets";
import { WpAsset } from "@hooks/use-get/assets/types";

interface AssetAutocompleteProps {
  value: number[] | string[] | any;
  onChange: (value: number[]) => void;
  label?: string;
  disabled?: boolean;
}

export const AssetAutocomplete = ({
  value,
  onChange,
  label = "Asset Tag",
  disabled,
}: AssetAutocompleteProps) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedAsset, setSelectedAsset] = useState<WpAsset | null>(null);
  const { items: assets, loading, getAssets, getOneAsset } = useGetAssets();

  const selectedAssetId = useMemo(() => {
    const firstValue = Array.isArray(value) ? value[0] : value;

    const id =
      firstValue && typeof firstValue === "object"
        ? (firstValue as Record<string, any>).id
        : firstValue;

    const parsed = Number(id);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [value]);

  const options = useMemo(() => {
    const list = assets || [];
    if (!selectedAsset) return list;
    const exists = list.some((asset) => asset.id === selectedAsset.id);
    return exists ? list : [selectedAsset, ...list];
  }, [assets, selectedAsset]);
  
  // Keep selected value resolvable even when the linked asset is not in the
  // current search results.
  const selectedValue = useMemo(() => {
    if (!selectedAssetId) return null;
    return options.find((asset) => asset.id === selectedAssetId) || null;
  }, [selectedAssetId, options]);

  useEffect(() => {
    const fetchAssets = async () => {
      await getAssets({ searchValue: inputValue });
    };

    const timeoutId = setTimeout(() => {
      fetchAssets();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, getAssets]);

  useEffect(() => {
    const hydrateSelectedAsset = async () => {
      if (!selectedAssetId) {
        setSelectedAsset(null);
        return;
      }

      const existing = (assets || []).find((asset) => asset.id === selectedAssetId);
      if (existing) {
        setSelectedAsset(existing);
        return;
      }

      const fetched = await getOneAsset(selectedAssetId);
      setSelectedAsset(fetched || null);
    };

    hydrateSelectedAsset();
  }, [selectedAssetId, assets, getOneAsset]);

    // Intentionally avoid include-filter fetches here.
    // The assets endpoint can reject transformed include params with 400,
    // which should not block loading the edit ticket modal.

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option: WpAsset) => option.title?.rendered || ""}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      filterOptions={(x) => x} // Filtering is handled by API
      loading={loading}
      value={selectedValue}
      disabled={disabled}
      onChange={(_, newValue: WpAsset | null) => {
        onChange(newValue ? [newValue.id] : []);
      }}

      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      slots={{
        paper: (props) => (
          <Paper {...props} elevation={8} />
        ),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth
          onKeyDown={(event) => {
            if (event.key === "Delete" || event.key === "Backspace") {
              setInputValue("");
              onChange([]);
            }
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default AssetAutocomplete;
